import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import {
  PriceTable as PriceTableType,
  ColumnHeader,
  PriceRow,
  PriceCell,
} from "@/queries/models"
import { formatCurrencyWithoutSuffix } from "@/utils/priceUtils"

import {
  ContentCell,
  HeaderCell,
  PrimaryText,
  SecondaryText,
  TitleCell,
  UnitCell,
} from "./Table.styles"

interface PriceTableProps {
  rowsTitle: string
  dataTitle: { primary: string; secondary?: string }
  currency: string
  table: PriceTableType
}

const PriceTable = ({
  table,
  rowsTitle,
  dataTitle,
  currency,
}: PriceTableProps) => {
  const { t } = useTranslation()
  return (
    <TableContainer component={Paper} square elevation={0}>
      <Table aria-label="Price table">
        <TableHead>
          <TableRow>
            <HeaderCell aria-hidden="true" />
            {table.columnHeaders.map((header: ColumnHeader) => (
              <HeaderCell key={header.label}>
                <PrimaryText>{header.label}</PrimaryText>
                {header.subLabel && (
                  <SecondaryText whiteSpace="pre-line">
                    {header.subLabel}
                  </SecondaryText>
                )}
              </HeaderCell>
            ))}
          </TableRow>
          <TableRow>
            <UnitCell>
              <PrimaryText>{rowsTitle}</PrimaryText>
            </UnitCell>
            <UnitCell>
              <PrimaryText>{dataTitle.primary}</PrimaryText>
              <SecondaryText>{dataTitle.secondary}</SecondaryText>
            </UnitCell>
            <UnitCell
              colSpan={table.columnHeaders.length - 1}
              aria-hidden="true"
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {table.rows.map((row: PriceRow) => (
            <TableRow key={row.title}>
              <TitleCell component="th">{row.title}</TitleCell>
              {row.prices.map((price: PriceCell | null, index: number) => (
                <ContentCell key={`${row.title}-${index}`}>
                  {price && (
                    <>
                      <PrimaryText>
                        {formatCurrencyWithoutSuffix(price.online, currency)}
                        {row.isSubscriptionProduct &&
                          t("subscriptionPriceSuffix").toLowerCase()}
                      </PrimaryText>

                      {price.sameDayPrice && (
                        <SecondaryText>
                          (
                          {formatCurrencyWithoutSuffix(
                            price.sameDayPrice,
                            currency
                          )}
                          )
                        </SecondaryText>
                      )}
                    </>
                  )}
                </ContentCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default PriceTable
